import { useFormContext, useWatch } from 'react-hook-form';

import { Label } from '@/components/ui/label';
import type { SiemFormValues } from './types';

/**
 * Which event streams get forwarded to this destination (KIR-4083).
 *
 * Two independent switches rather than a single "what to send" picker, because
 * the two streams are genuinely unrelated: alerts are security findings graded
 * by severity, audit events are a record of who changed what. A team can
 * reasonably want either, both, or — briefly, while testing a destination —
 * neither.
 *
 * The checkbox pattern matches the SSL toggle in `DestinationFields` rather than
 * introducing a Switch, so the modal reads as one form.
 */
export function ForwardedEventsField() {
  const { control, setValue } = useFormContext<SiemFormValues>();
  const forwardAlerts = useWatch({ control, name: 'forwardAlerts' });
  const forwardAuditLogs = useWatch({ control, name: 'forwardAuditLogs' });

  return (
    <div className="space-y-3">
      <Label className="font-semibold">Forwarded Events</Label>

      <div className="flex items-start gap-2">
        <input
          id="siem-forward-alerts"
          type="checkbox"
          className="mt-1 accent-primary"
          checked={forwardAlerts}
          onChange={e =>
            setValue('forwardAlerts', e.target.checked, { shouldDirty: true })
          }
        />
        <div className="space-y-0.5">
          <Label htmlFor="siem-forward-alerts" className="font-semibold">
            Security alerts
          </Label>
          <p className="text-xs text-foreground/80">
            Findings from scans, at or above the minimum severity above.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2">
        <input
          id="siem-forward-audit-logs"
          type="checkbox"
          className="mt-1 accent-primary"
          checked={forwardAuditLogs}
          onChange={e =>
            setValue('forwardAuditLogs', e.target.checked, {
              shouldDirty: true,
            })
          }
        />
        <div className="space-y-0.5">
          <Label htmlFor="siem-forward-audit-logs" className="font-semibold">
            Audit logs
          </Label>
          <p className="text-xs text-foreground/80">
            Configuration and access changes — who changed what, and when.
            Batched and sent about once a minute, so allow a minute or two
            before a change appears in your SIEM. The minimum severity above
            does not apply: audit events have no severity.
          </p>
          {forwardAuditLogs && !forwardAlerts && (
            <p className="text-xs text-foreground/80">
              Alerts are off, so only audit events will be forwarded.
            </p>
          )}
        </div>
      </div>

      {/*
        A warning, not a validation error. Both streams off is a legal
        configuration — someone provisioning a destination ahead of turning it on
        has a real reason to save one — and `siemFormResolver` lets it through.
        Styling it `text-destructive` said "you cannot save this" while the Save
        button stayed enabled, which is the worse of the two lies. Matches the
        inline amber warning in `ConnectSamlSsoModal`.
      */}
      {!forwardAlerts && !forwardAuditLogs && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400">
          Nothing will be forwarded. This destination can be saved, but it stays
          silent until you select an event type.
        </p>
      )}
    </div>
  );
}
